<template>
  <div
    v-observe-visibility="visibilityChanged"
    class="board-item-list-card"
    :class="{
      'drop-target': cardIsDropTarget,
    }"
    @mouseenter="displayPopover"
    @mouseleave="hidePopover"
    @click="openCard"
  >
    <div
      v-if="visible"
      class="board-item-list-card-content"
      :class="{ 'card-grid-view': gridView, 'bg-highlight-bg': hasChildCards }"
    >
      <v-popover
        v-if="renderPopover"
        ref="popoverRef"
        :role="'poper'"
        tag="div"
        :offset="[88, 20]"
        content-tag="div"
        :on-show="() => !draggingIsOn"
        content-class="card-popover"
        placement="top-start"
        :trigger="'manual'"
      >
        <template #content="{ state }">
          <BoardListCard
            v-if="state.isVisible"
            :card-id="cardId"
            :index="0"
          />
        </template>
      </v-popover>
      <div class="board-item-card-title">
        {{ card.title }}
      </div>
    </div>
    <CancelDragLabel />
  </div>
</template>

<script>
import boardListCardMixin from '@/mixins/boardListCardMixin';
import BoardListCard from '@/components/widgets/BoardListCard';
import cardPreviewPopoverMixin from '@/mixins/cardPreviewPopoverMixin';
import childCardCounterMixin from '@/mixins/childCardCounterMixin';
import CancelDragLabel from '@/components/widgets/CancelDragLabel.vue';
import getDashedTitle from '@/utilities/getDashedTitle';
import EventBus from '../../utilities/eventBus';
import { getSprintKey } from '@/utilities/sprintKeys';

export default {
  name: 'ListOfBoardsItemCard',
  components: {
    BoardListCard,
    CancelDragLabel,
  },
  mixins: [boardListCardMixin, cardPreviewPopoverMixin, childCardCounterMixin],
  props: {
    cardId: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    isTemplate: {
      // used in childCardCounterMixin
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/no-unused-properties
    listCards: {
      // used in boardListCardMixin
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      displayCardDetails: false,
    };
  },
  computed: {
    totalNumberOfChecks() {
      const checklists = this.card.checklists || [];
      let count = 0;
      checklists.forEach((cl) => {
        count += (cl.items || []).length;
      });
      return count;
    },
    decodedCardTitle() {
      return this.card.title || '';
    },

    onDifferentRoute() {
      return (
        this.$route.name !== this.$constants.routeNames.space ||
        this.$store.getters.displayProjectPreviewBoards
      );
    },
    cardIsDropTarget() {
      if (this.onDifferentRoute) return false;
      return this.$store.getters.dropTargetId === `c-${this.card.id}`;
    },

    draggingEnd() {
      return this.$store.getters.getDraggingEnd;
    },

    card() {
      return this.$store.getters.getCards[this.cardId] || {};
    },

    gridView() {
      return this.$store.getters.getCurrentProjectBoardColumnLayout === 'grid';
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
  },
  watch: {
    showPopover(newVal) {
      this.$refs.popoverRef?.[newVal ? 'show' : 'hide']?.();
    },
  },
  created() {
    if (this.gridView) {
      // 15 is the number of cards that are visible in the grid view on load
      this.visible = this.index < 15;
    }
  },
  beforeUnmount() {
    clearTimeout(this.renderTimeout);
  },
  methods: {
    openCard() {
      const cardParentId = this.card.board_id
        ? this.card.board_id
        : getSprintKey(this.card.project_id, this.card.sprint_id);
      EventBus.$emit('cardClicked', cardParentId);
      if (this.draggingEnd) return;
      this.hidePopover();
      this.setCurrentCard();
      this.$store.dispatch('setPreviewPopoverKey', false);
      if (this.card && Object.keys(this.card).length) {
        const dashedCardTitle = getDashedTitle(this.card.title);
        const cardRouteType = this.card.sprint_id ? 'sprintcard' : 'card';
        this.$router.push({
          path: `/${this.teamSlug}/${cardRouteType}-${this.card.id}-${dashedCardTitle}`,
          query: { userNavigated: true },
        });
      }
    },
    displayPopover() {
      if (this.draggingIsOn || this.popOverEnabled) return;
      this.popOverEnabled = true;
    },
    hidePopover() {
      if (!this.popOverEnabled) return;
      this.popOverEnabled = false;
    },
    visibilityChanged(visible) {
      if (!visible) return;
      this.delayedRender();
      this.visible = visible;
    },
    delayedRender() {
      this.renderTimeout = setTimeout(() => {
        this.renderPopover = true;
      }, 400);
    },
  },
};
</script>

<style lang="scss">
.board-item-list-card {
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  margin: $marginBetweenListCards $boardColumnPaddingRight $marginBetweenListCards
    $boardColumnPaddingLeft;
  min-height: $listOfBoardsSmallCardHeight;

  &:last-child {
    margin-bottom: 0px;
  }

  &.drop-target {
    border: 1px solid #929292;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.list-card-drop {
    border: none;
    .card-item-separator {
      display: none;
    }
  }

  .board-item-list-card-content {
    box-sizing: border-box;
    height: $listOfBoardsSmallCardHeight;
    width: $columnWidth;
    border: 1px solid;
    border-radius: 4px;
    padding: 3px 7px;
    display: flex;
    flex-direction: column;
    flex: 1;
    transition:
      background,
      box-shadow 0.3s;
    @apply bg-important-bg border-important-border;

    &:hover {
      background: var(--importantElHover) !important;
    }

    .board-item-card-title {
      font-size: 14px;
      line-height: 20px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      @apply text-text-default;
    }

    .board-item-card-bottom {
      margin-top: 8px;
      display: flex;
      flex-direction: row;
      justify-self: flex-end;
      justify-content: space-between;
    }

    > .v-popover {
      height: 0;
    }
  }
}

.list-card-drop {
  &.board-item-list-card {
    .board-item-list-card-content {
      box-shadow: none;
      border: none;
      background-color: var(--importantElHover) !important;
      width: $columnWidth;
      > * {
        visibility: hidden;
      }
    }
  }
}

.board-list-cards {
  .list-card-drop {
    &.list-row {
      &.sortable-chosen {
        > .board-list-card-wrap,
        .timeline-list-item > .board-list-card-wrap {
          max-height: $listOfBoardsSmallCardHeight;
          max-width: $columnWidth;
          box-shadow: none;
          border: none;
          @apply bg-important-hover;
          > * {
            visibility: hidden;
          }
        }
        .timeline-list-item {
          .timeline-item-container {
            display: none;
          }
        }
      }
    }
  }
}

.tippy-box {
  .tippy-content {
    .card-popover {
      width: $columnWidth;
    }
  }
}
</style>
